import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ListQuery } from '@tww/shared';

/** Listen-Zustand (Filter, Sortierung, Seite, Suche) im URL-Zustand */
export function useListState(defaults: { sort?: string; order?: 'asc' | 'desc'; pageSize?: number } = {}) {
  const [sp, setSp] = useSearchParams();
  // Jüngster Stand auch zwischen zwei Renderings: Änderungen kurz nacheinander (Zurücksetzen, dann Tippen) bauen aufeinander auf
  const latest = useRef(sp);
  latest.current = sp;
  const state = useMemo(() => {
    const filters: Record<string, string[]> = {};
    for (const [k, v] of sp.entries()) {
      const m = /^f\.(.+)$/.exec(k);
      if (m) filters[m[1]!] = v.split(',').filter(Boolean);
    }
    return {
      q: sp.get('q') ?? '',
      sort: sp.get('sort') ?? defaults.sort,
      order: (sp.get('order') as 'asc' | 'desc' | null) ?? defaults.order,
      page: Number(sp.get('page') ?? 1),
      pageSize: Number(sp.get('pageSize') ?? defaults.pageSize ?? 50),
      filters,
    };
  }, [sp, defaults.sort, defaults.order, defaults.pageSize]);

  const update = useCallback(
    (patch: Partial<{ q: string; sort: string; order: 'asc' | 'desc'; page: number; filters: Record<string, string[]> }>) => {
      const n = new URLSearchParams(latest.current);
      if (patch.q !== undefined) {
        if (patch.q) n.set('q', patch.q);
        else n.delete('q');
      }
      if (patch.sort !== undefined) n.set('sort', patch.sort);
      if (patch.order !== undefined) n.set('order', patch.order);
      if (patch.filters !== undefined) {
        for (const k of Array.from(n.keys())) if (k.startsWith('f.')) n.delete(k);
        for (const [k, v] of Object.entries(patch.filters)) if (v.length) n.set(`f.${k}`, v.join(','));
      }
      if (patch.page !== undefined && patch.page > 1) n.set('page', String(patch.page));
      else if (patch.page !== undefined || patch.filters !== undefined || patch.q !== undefined) n.delete('page');
      latest.current = n;
      setSp(n, { replace: true });
    },
    [setSp],
  );

  const filtersOf = (p: URLSearchParams) => {
    const out: Record<string, string[]> = {};
    for (const [k, v] of p.entries()) if (k.startsWith('f.')) out[k.slice(2)] = v.split(',').filter(Boolean);
    return out;
  };
  const setFilter = useCallback((key: string, values: string[]) => update({ filters: { ...filtersOf(latest.current), [key]: values } }), [update]);
  const toggleSort = useCallback(
    (key: string) => {
      const p = latest.current;
      update({ sort: key, order: p.get('sort') === key && (p.get('order') ?? 'asc') === 'asc' ? 'desc' : 'asc' });
    },
    [update],
  );

  const apiParams = useMemo(() => {
    const p = new URLSearchParams();
    if (state.q) p.set('q', state.q);
    if (state.sort) p.set('sort', state.sort);
    if (state.order) p.set('order', state.order);
    p.set('page', String(state.page));
    p.set('pageSize', String(state.pageSize));
    for (const [k, v] of Object.entries(state.filters)) if (v.length) p.set(`filter[${k}]`, v.join(','));
    return p.toString();
  }, [state]);

  return { state: state as ListQuery & { filters: Record<string, string[]> }, update, setFilter, toggleSort, apiParams };
}
